import {
    Document,
    Image,
    Link,
    Page,
    StyleSheet,
    Text,
    View
} from "@react-pdf/renderer"

import JagadLogoSrc from '../../assets/images/PdfJagadLogo.png'
import CurrencyLogo from '../../assets/images/PdfCurrencyLogo.png'
import FooterBackground from '../../assets/images/PdfFooterBackground.png'
import GreenChecklist from '../../assets/images/PdfGreenChecklist.png'

import {
    BLACK,
    BLUE,
    DARK_VIOLET,
    GRAYSCALE_400,
    GREEN_600,
    NEUTRAL_100,
    PALE_BLUE
} from "../../styles/colors"
import { formatDate, toCurrencyFormat } from "@jagad/utils"

const styles = StyleSheet.create({
    // Layout styles
    page: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    headerContainer: {
        padding: 16,
    },
    transactionInfoHeader: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'center',
        gap: 8
    },
    sectionContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        gap: 12
    },
    bodyContainer: {
        padding: 16,
        gap: 36,
        display: 'flex',
        flexDirection: 'column',
        width: '100%'
    },
    rowContainer: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    statusContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        color: GREEN_600,
        fontWeight: 'bold',
        fontSize: 16
    },
    footer: {
        position: 'relative',
        height: '10%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'flex-start',
    },
    footerText: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        marginLeft: 16,
        fontSize: 14,
    },
    footerImage: {
        width: '100%',
        position: 'absolute',
    },

    // Card and container styles
    bigAmountContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        borderColor: NEUTRAL_100,
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 8,
        backgroundColor: PALE_BLUE,
        gap: 8
    },
    detailsCard: {
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderColor: NEUTRAL_100,
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 8,
        gap: 12
    },
    divider: {
        width: '100%',
        height: 1,
        backgroundColor: NEUTRAL_100
    },
    bigAmount: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 16,
    },

    // Image styles
    logoImage: {
        height: 36
    },
    currencyLogo: {
        width: 24,
        height: 24
    },
    checklistIcon: {
        width: 16,
        height: 16
    },

    // Typography styles
    sectionTitle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: DARK_VIOLET
    },
    fieldName: {
        fontSize: 16,
        fontWeight: 'bold',
        color: GRAYSCALE_400
    },
    fieldValue: {
        fontSize: 16,
        color: BLACK
    },
    fieldValueBold: {
        fontSize: 16,
        fontWeight: 'bold',
        color: BLACK
    },
    transactionId: {
        fontWeight: 'bold',
        fontSize: 13,
    },
    transactionDate: {
        color: GRAYSCALE_400,
        fontSize: 13,
    },
    amountLabel: {
        marginTop: 16,
        color: GRAYSCALE_400,
        fontSize: 16,
    },
    amountValue: {
        fontSize: 24,
        fontWeight: 'bold'
    },
    linkStyle: {
        color: BLUE,
        textDecoration: 'underline',
    },
    transactionDetailsContainer: {
        paddingHorizontal: 16,
        paddingVertical: 16,
        borderColor: NEUTRAL_100,
        width: '100%',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRadius: 8,
        gap: 12
    },
    transactionDetailContainerWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
        gap: 12
    }
})

export type PDFRendererProps = {
    transactionId: string,
    transactionDate: string,
    amount: string,
    bank: string,
    accountNumber: string,
    accountName: string,
    status: string,
    fees: string,
    totalAmount: string
}

const PDFRenderer = ({
    transactionId,
    transactionDate,
    amount,
    bank,
    accountNumber,
    accountName,
    status,
    fees,
    totalAmount
}: PDFRendererProps) => {
    return (
        <Document>
            <Page size="A4" style={styles.page}>

                {/* Header */}
                <View
                    style={styles.headerContainer}
                >
                    <View style={styles.header}>
                        <Image
                            src={JagadLogoSrc}
                            style={styles.logoImage}
                        />
                        <View style={styles.transactionInfoHeader}>
                            <Text
                                style={styles.transactionId}
                            >
                                Transaction ID: {transactionId}
                            </Text>
                            <Text
                                style={styles.transactionDate}
                            >
                                {formatDate(transactionDate, true)}
                            </Text>
                        </View>
                    </View>



                </View>

                {/* Body */}
                <View
                    style={styles.bodyContainer}
                >

                <View
                    style={styles.bigAmountContainer}
                >
                    <Text
                        style={styles.amountLabel}
                    >
                        Amount paid
                    </Text>
                    <View
                        style={styles.bigAmount}
                    >
                        <Image
                            src={CurrencyLogo}
                            style={styles.currencyLogo}
                        />
                        <Text
                            style={styles.amountValue}
                        >
                            {toCurrencyFormat(Number(amount))}
                        </Text>
                    </View>
                </View>

                <View
                    style={styles.transactionDetailContainerWrapper}
                >
                    <Text style={styles.sectionTitle}>Transaction details</Text>
                    <View
                        style={styles.transactionDetailsContainer}
                    >
                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>
                                Status
                            </Text>
                            <View
                                style={styles.statusContainer}
                            >
                                <Text>{status}</Text>
                                <Image
                                    src={GreenChecklist}
                                    style={styles.checklistIcon}
                                />
                            </View>
                        </View>
                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>Bank</Text>
                            <Text style={styles.fieldValue}>{bank}</Text>
                        </View>
                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>Account number</Text>
                            <Text style={styles.fieldValue}>{accountNumber}</Text>
                        </View>
                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>Account name</Text>
                            <Text style={styles.fieldValue}>{accountName}</Text>
                        </View>
                    </View>
                </View>

                <View
                    style={styles.transactionDetailContainerWrapper}
                >
                    <Text style={styles.sectionTitle}>Amount details</Text>
                    <View
                        style={styles.transactionDetailsContainer}
                    >
                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>Amount</Text>
                            <Text style={styles.fieldValue}>{`Rp${toCurrencyFormat(Number(amount))}`}</Text>
                        </View>
                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>Fees</Text>
                            <Text style={styles.fieldValue}>{`Rp${toCurrencyFormat(Number(fees))}`}</Text>
                        </View>

                        <View
                            style={styles.divider}
                        />

                        <View
                            style={styles.rowContainer}
                        >
                            <Text style={styles.fieldName}>Total amount</Text>
                            <Text style={styles.fieldValueBold}>{`Rp${toCurrencyFormat(Number(totalAmount))}`}</Text>
                        </View>
                    </View>
                </View>

                </View>

                {/* Footer */}
                <View
                    style={styles.footer}
                >
                    <View
                        style={styles.footerText}
                    >
                        <Text>
                            For any questions, contact us at:
                        </Text>
                        <Link
                            style={styles.linkStyle}
                            href="mailto:support@garuda.to"
                        >
                            support@garuda.to
                        </Link>
                    </View>
                    <Image
                        src={FooterBackground}
                        style={styles.footerImage}
                    />
                </View>

            </Page>
        </Document>
    )
}

export default PDFRenderer
